import type { VintedIntegration } from "src/interfaces/plaidIntegration.interface";
import type MonthYear from "src/interfaces/monthYear.interface";
import type { SyncLock } from "../syncLock";
import VintedClient from "./VintedClient";
import {
  attachResolvedThreadIdsToSoldLines,
  filterWalletLinesForKnownSales,
  getInvoiceLineId,
  uploadTransactionsBatch,
  type VintedImportType,
} from "./vintedBatches";

/**
 * Wallet / transactions import via `getWalletInvoicesFull`.
 * Optional `onMonthComplete` checkpoints after each fully uploaded month
 * (used by resumable direct import).
 *
 * When `knownSaleTransactionIds` is set, Sold lines whose `entity_id` (or
 * resolved aliases) matches are omitted so they cannot stub a sale already
 * covered by the sales upload.
 *
 * Sold lines that are uploaded get `mrg_thread_ids` attached (UUID +
 * canonical) so the backend can find sales keyed under either shape.
 */
export async function syncTransactionsForMonths(
  client: VintedClient,
  integration: VintedIntegration,
  months: Iterable<{ year: number; month: number }>,
  importType: VintedImportType,
  {
    timestamp,
    uploadTransactionsId,
    batchIndex: startBatchIndex = 0,
    knownSaleTransactionIds,
    threadIdByAnyId,
  }: {
    timestamp: number;
    uploadTransactionsId?: string;
    batchIndex?: number;
    knownSaleTransactionIds?: Set<string>;
    /** Optional enrich-built map: any thread id → all aliases for that order. */
    threadIdByAnyId?: Map<string, string[]>;
  },
  lock: SyncLock,
  onMonthComplete?: (month: MonthYear, batchIndex: number) => Promise<void>
): Promise<number> {
  let batchIndex = startBatchIndex;
  const seenLineIds = new Set<string>();

  for (const { year, month } of months) {
    lock.throwIfAborted();
    for await (const lines of client.getWalletInvoicesFull({ year, month })) {
      const newLines = filterWalletLinesForKnownSales(
        lines.filter((line) => {
          const id = getInvoiceLineId(line);
          if (!id) return true;
          if (seenLineIds.has(id)) return false;
          seenLineIds.add(id);
          return true;
        }),
        knownSaleTransactionIds
      );

      const linesWithThreadIds = await attachResolvedThreadIdsToSoldLines(
        newLines,
        {
          threadIdByAnyId,
          resolveConversationId: async (threadId) => {
            lock.throwIfAborted();
            const res = await client.getConversation(threadId);
            const id = (res as { conversation?: { id?: unknown } })
              ?.conversation?.id;
            return id == null || id === "" ? null : String(id);
          },
        }
      );

      await uploadTransactionsBatch(
        integration,
        linesWithThreadIds,
        importType,
        {
          uploadTransactionsId,
          timestamp,
          batchIndex: batchIndex++,
        },
        lock
      );
    }

    await onMonthComplete?.({ year, month } as MonthYear, batchIndex);
  }

  return batchIndex;
}
